/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { inject } from "mobx-react";
import { injectIntl } from "react-intl";
import {
  getTotalPages,
  getCurrMaxPageWidth,
  getShouldRecomputePageSizes,
  getUseReactVirtualized,
  isTwoColumnViewMode,
  getBottomPanelHeight
} from "selectors/documentSelectors";
import { getIsInPlaceMode } from "selectors/viewModeSelectors";
import { getPagesViewYPos, getPagesViewXPos, isPagedViewScrolling } from "selectors/pagedViewState";
import { pagesInView } from "selectors/pagedViewSelectors";
import { PagesView } from "components/document/page/PagesView";
import { NonPDFPageContainer } from "containers/document/page/NonPDFPageContainer";
import * as constants from "constants/constants";
import { getPageHeight, getPageWidth, getTotalRows, getPageIndex } from "selectors/pageSelectors";
import { getViewPortHeight, getViewPortWidth } from "selectors/documentViewSelectors";
import { getMargin } from "selectors/zoom";
import {
  getShouldUseModernViewer,
  getShouldUseMobileModernViewer,
  getHideStickyToolOnTap
} from "selectors/appSelectors";
import { getScrollbarSize } from "selectors/appUiStateSelectors";
import { getCurrentPageIndex } from "selectors/pageNumber";
import { getOpenAction } from "selectors/pagedViewState";

const mapStoreToProps = (stores, ownProps) => {
  const isModernViewer = getShouldUseModernViewer(stores);
  return {
    pagesInViewStart: pagesInView(stores).start,
    pagesInViewEnd: pagesInView(stores).end,
    pageCount: getTotalPages(stores),
    PageContainer: NonPDFPageContainer,
    pagesViewYPos: getPagesViewYPos(stores),
    pagesViewXPos: getPagesViewXPos(stores),
    isScrolling: isPagedViewScrolling(stores),
    setFocusMethod: ownProps.setFocusMethod,
    topPadding: constants.PAGES_TOP_PADDING,
    onScroll: event => {
      stores.pagedViewState.onPagesViewScroll(event);
    },
    onPagesInViewChange: pagesInView => {
      stores.documentStore.loadPagesInView(pagesInView);
    },
    getPageViewHeight: ({ index }) => getPageHeight(stores, index) + constants.PAGE_BOTTOM_MARGIN,
    parentViewHeight: getViewPortHeight(stores),
    parentViewWidth: getViewPortWidth(stores),
    getMaxWidth: () => {
      const margins = getMargin(stores);
      let retVal = getCurrMaxPageWidth(stores);
      if (margins.left !== "auto" && margins.right !== "auto") {
        const sumOfMargins = Number(margins.left + margins.right);
        retVal += sumOfMargins;
        retVal = Math.max(getViewPortWidth(stores), retVal);
      }
      let scrollbarSize = getScrollbarSize(stores);
      if (!scrollbarSize) {
        // Subtract 15px to the width to adjust for the additional 15px as added in _getFixedWidthValues function in AppController
        scrollbarSize = 15;
      }
      return retVal - (isModernViewer ? scrollbarSize : 0);
    },
    shouldRecomputePageSizes: getShouldRecomputePageSizes(stores) === true,
    resetShouldRecomputePageSizes: () => {
      stores.documentStore.setShouldRecomputePageSizes(false);
    },
    useReactVirtualized: getUseReactVirtualized(stores),
    overscanRowCount: constants.DOCUMENT_OVERSCAN_ROW_COUNT,
    onSectionRendered: ({ rowStartIndex, rowStopIndex }) => {
      const pagesInView = {
        start: rowStartIndex,
        end: rowStopIndex
      };
      stores.documentStore.loadPagesInView(pagesInView);
    },
    estimatedRowSize: getPageHeight(stores, 0) + constants.PAGE_BOTTOM_MARGIN,
    estimatedColumnSize: getPageWidth(stores, 0),
    setNonReactVPagesViewHandle: handle => {
      stores.pagedViewState.setNonReactVPagesViewHandle(handle);
    },
    shouldHighlightGridOnFocus:
      isModernViewer && !getShouldUseMobileModernViewer(isModernViewer) && !getIsInPlaceMode(stores),
    totalRows: getTotalRows(stores),
    getPageIndex: (rowIndex, columnIndex) => getPageIndex(stores, rowIndex, columnIndex),
    columnCount: isTwoColumnViewMode(stores) === true ? 2 : 1,
    loadNonPDFPageCanvas: isModernViewer,
    isModernViewerEnabled: isModernViewer,
    bottomPanelHeight: getBottomPanelHeight(stores),
    shouldUseMobileModernViewer: getShouldUseMobileModernViewer(stores),
    currPageIndex: getCurrentPageIndex(stores),
    onPagesViewMount: () => {
      const { pageIndex, x, y } = getOpenAction(stores);
      stores.pagedViewState.goToLocation(pageIndex, x, y);
    },
    hideStickyToolOnTap: getHideStickyToolOnTap(stores)
  };
};

const NonPDFContainer = injectIntl(inject(mapStoreToProps)(PagesView));

export { NonPDFContainer };
